import { BoxOfferDetailedEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferInvitationStatus, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxOfferInvitationCoupon, BoxOfferInvitationDto } from "@modules/boxOffer/gateway/boxOfferDto"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { convertUserFromDto } from "@modules/profile/profileDtos"
import { User } from "@modules/profile/profileTypes"
import { DefaultTheme } from "styled-components"

export type BoxOfferInvitationState = Omit<
  BoxOfferInvitationEntity,
  "getColorStatus" | "getPriceAfterDicount" | "getWordingStatus" | "getWordingCouponDate"
>
export class BoxOfferInvitationEntity {
  id: string
  boxOfferId: string
  invitee: User
  inviter: User
  status: BoxOfferInvitationStatus
  createdAt: Date
  coupon: BoxOfferInvitationCoupon | null
  deferredDate: Date | null
  updatedAtDate: Date | null

  private constructor(invitation: BoxOfferInvitationState) {
    this.id = invitation.id
    this.boxOfferId = invitation.boxOfferId
    this.invitee = invitation.invitee
    this.inviter = invitation.inviter
    this.status = invitation.status
    this.createdAt = invitation.createdAt
    this.coupon = invitation.coupon
    this.deferredDate = invitation.deferredDate
    this.updatedAtDate = invitation.updatedAtDate
  }

  static fromDto(dto: BoxOfferInvitationDto): BoxOfferInvitationEntity {
    return new BoxOfferInvitationEntity({
      ...dto,
      invitee: convertUserFromDto(dto.invitee),
      inviter: convertUserFromDto(dto.inviter),
      createdAt: new Date(dto.createdAt),
      deferredDate: dto.deferredDate ? new Date(dto.deferredDate) : null,
      updatedAtDate: dto.updatedAtDate ? new Date(dto.updatedAtDate) : null,
    })
  }

  getColorStatus = (theme: DefaultTheme) => {
    switch (this.status) {
      case BoxOfferInvitationStatus.PENDING:
        return theme.warning
      case BoxOfferInvitationStatus.ACCEPTED:
        return theme.sucess
      case BoxOfferInvitationStatus.REFUSED:
      case BoxOfferInvitationStatus.CANCELLED:
        return theme.error
    }
  }

  getWordingStatus = () => {
    switch (this.status) {
      case BoxOfferInvitationStatus.PENDING:
        return word("box.members.tab.offer.status.pendingInvitation")
      case BoxOfferInvitationStatus.ACCEPTED:
        return word("box.members.tab.offer.status.ongoing")
      case BoxOfferInvitationStatus.REFUSED:
        return word("box.members.tab.offer.status.declinedInvitation")
      case BoxOfferInvitationStatus.CANCELLED:
        return word("box.members.tab.offer.status.canceledInvitation")
      default:
        return ""
    }
  }

  getWordingCouponDate(offer: BoxOfferDetailedEntity): string {
    if (!this.coupon || offer.type !== BoxOfferType.Subscription) return ""

    if (this.coupon.durationInMonths === null) {
      return word("boxOffer.invitation.coupon.date.unlimited")
    }
    return word("boxOffer.invitation.coupon.date.durationInMonths", { count: this.coupon.durationInMonths })
  }

  getPriceAfterDicount = (price: number): number => {
    if (!this.coupon || this.coupon.value <= 0) return price

    if (this.coupon.type === CouponType.PERCENT) {
      return price - (price * this.coupon.value) / 100
    } else {
      return price - this.coupon.value
    }
  }
}
